import { useEffect, useState } from "react";
import { SupportLookOnlyNote } from "@/components/SupportLookOnlyNote";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { KeyRound, Loader2, DownloadCloud, Users, ShieldCheck, Lock } from "lucide-react";
import UsersManager from "@/components/UsersManager";

const CRIMSON = "var(--brand)";

export default function AuthSettings() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.authNativeStatus().then(setStatus).catch(() => {});
  useEffect(() => { load(); }, []);

  const seed = async () => {
    setBusy("seed");
    try { const r = await api.authNativeSeed(); toast.success(`Seeded ${r.seeded} users (${r.created} new, ${r.updated} updated).`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Seed failed."); }
    finally { setBusy(""); }
  };
  const flip = async (checked) => {
    const nextSrc = checked ? "native" : "legacy";
    if (checked && !window.confirm("Switch sign-in to our own database? New logins will validate against native accounts (users set a password on first sign-in). You can switch back instantly.")) return;
    try { await api.authNativeSetSource(nextSrc); toast.success(`Sign-in now uses ${nextSrc === "native" ? "our database" : "the legacy source"}.`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };

  const isNative = status?.source === "native";
  const seeded = status?.seeded || 0;

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="auth-settings">
      <div className="flex items-center gap-2.5 mb-1"><KeyRound className="h-5 w-5" style={{ color: CRIMSON }} /><h1 className="text-xl font-semibold font-brand text-slate-800">Authentication</h1></div>
      <SupportLookOnlyNote />
      <p className="text-sm text-slate-500 mb-5">App-native sign-in — email + password (bcrypt) with 2-step verification (authenticator app, email or SMS OTP). Each person sets their password on first sign-in via a welcome link. Flip this per environment; existing sessions keep working until they expire.</p>

      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4" data-testid="auth-source-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold font-brand text-slate-800">Sign-in source</h3>
            <Badge className={`border ${isNative ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{isNative ? "Our DB (native)" : "Legacy source"}</Badge>
          </div>
          <label className="flex items-center gap-2 text-[11px] text-slate-500" title={seeded === 0 ? "Seed users first" : ""}>
            {seeded === 0 && !isNative && <Lock className="h-3 w-3 text-slate-400" />}
            Use our database
            <Switch checked={isNative} disabled={seeded === 0 && !isNative} onCheckedChange={flip} data-testid="auth-source-toggle" />
          </label>
        </div>
        {seeded === 0 && <p className="mt-1 text-[11px] text-slate-400">Seed the user list to unlock native sign-in, or use the Users Manager to create accounts manually.</p>}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="auth-seed-card">
        <div className="flex items-center gap-2 mb-2"><Users className="h-4 w-4 text-slate-500" /><h3 className="text-sm font-semibold font-brand text-slate-800">Native user store</h3></div>
        <div className="flex flex-wrap gap-2 text-[11px] mb-3">
          <Badge className="border bg-white text-slate-600 border-slate-200" data-testid="seeded-count">Seeded {seeded}</Badge>
          <Badge className="border bg-amber-50 text-amber-700 border-amber-200" data-testid="pending-count">Awaiting first password {status?.pending_reset ?? 0}</Badge>
          <Badge className="border bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="twofa-count"><ShieldCheck className="h-3 w-3 mr-1" />2FA enrolled {status?.twofa_enrolled ?? 0}</Badge>
        </div>
        <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={seed} disabled={!!busy} data-testid="seed-users-btn">
          {busy === "seed" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <DownloadCloud className="h-4 w-4 mr-1.5" />}Seed users from source
        </Button>
        <p className="mt-2 text-[11px] text-slate-400">Copies all users (email, name, roles, mobile) from the source — no passwords transferred. Safe to re-run; existing passwords &amp; 2FA are preserved.</p>
      </div>

      <div className="mt-4"><UsersManager /></div>
    </div>
  );
}
